import { css } from "@emotion/react";
import { Button } from "primereact/button";
import { PinnedMessage as IPinnedMessage } from "../use-omnichannel";
import { AudioPlayer } from "./AudioPreview";
import { ParentId } from "./styles";
import { PinIcon } from "../../assets";

export interface PinnedMessageProps {
  pinnedMessage: IPinnedMessage;
  canRemove: boolean;
  onRemove: () => void;
  onClick?: () => void;
}

type Kind = "text" | "image" | "audio" | "video" | "other";

const PinnedMessage = ({
  pinnedMessage,
  canRemove,
  onRemove,
  onClick
}: PinnedMessageProps) => {
  const text = (pinnedMessage.message ?? "").trim();
  const mime = (pinnedMessage.mime ?? "").trim();

  const kind: Kind = (() => {
    if (mime.startsWith("image/")) return "image";
    if (mime.startsWith("audio/")) return "audio";
    if (mime.startsWith("video/")) return "video";
    if (mime) return "other";
    return "text";
  })();

  const renderContent = () => {
    if (mime) {
      if (mime.startsWith("image/")) {
        return (
          <div
            css={css`
              width: 100%;
              display: flex;
              justify-content: center;
            `}
          >
            <img
              src={text}
              height={120}
              css={css`
                max-width: 100%;
              `}
            />
          </div>
        );
      }

      if (mime.startsWith("audio/")) {
        return text ? (
          <div
            css={css`
              & > div {
                background: #fff;
                border-color: #b5b4c0;
              }
            `}
          >
            <AudioPlayer url={text} />
          </div>
        ) : (
          <div>Áudio fixado não carregado</div>
        );
      }

      if (mime.startsWith("video/")) {
        return text ? (
          <video
            src={`${text}#t=0.1`}
            preload="metadata"
            muted
            css={css`
              display: block;
              width: 100%;
              max-width: 220px;
              pointer-events: none;
            `}
          />
        ) : (
          <div>Vídeo fixado não carregado</div>
        );
      }

      return (
        <a
          href={text || undefined}
          target="_blank"
          rel="noreferrer"
          style={{ wordBreak: "break-all" }}
        >
          Arquivo
        </a>
      );
    }

    if (text) {
      return (
        <div
          css={css`
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          `}
        >
          {text}
        </div>
      );
    }

    return "Mensagem fixada não carregada";
  };

  return (
    <div
      onClick={(e) => {
        if ((e.target as HTMLElement).closest("audio, video, a, button")) {
          return;
        }
        onClick?.();
      }}
      css={css`
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 4px 1rem;
        border-bottom: 1px solid #e5e5e5;
        background: #fff;
        cursor: ${onClick ? "pointer" : "default"};

        @media screen and (max-width: 599px) {
          padding: 4px 10px;
        }
      `}
    >
      <img src={PinIcon} alt="Pin Icon" width={28} height={28} />
      <ParentId
        $variant={pinnedMessage.sender !== "CLIENT" ? "agent" : "client"}
        $kind={kind}
        css={css`
          flex: 1;
          min-width: 0;
          height: auto;

          ${kind !== "text" &&
          css`
            display: flex;
            flex-direction: column;
            align-items: center;
          `}
        `}
      >
        {renderContent()}
      </ParentId>
      {canRemove && (
        <Button
          icon="pi pi-times"
          className="p-button-text p-button-rounded p-button-secondary"
          aria-label="Desafixar mensagem"
          onClick={onRemove}
        />
      )}
    </div>
  );
};

export default PinnedMessage;
